import React, { useState, useEffect, useCallback } from 'react'
import { useNotificationActions } from '@/contexts/NotificationContext'
import { usePOSStore } from '@/store/posStore'
import { apiService } from '@/services/api'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { Wallet, ArrowDownLeft, ArrowUpRight, RefreshCw } from 'lucide-react'

// ─── Wallet Management Modal ─────────────────────────────────────────────────

interface WalletTx {
  id: string
  type: 'topup' | 'deduct' | 'refund'
  amount: number
  balance: number
  note: string
  transactionId?: string
  createdAt: string
}

interface WalletModalProps {
  customer: any
  liveBalance: number
  onClose: () => void
  onTopUp: (amount: number, reason: string, note: string) => Promise<void>
}

const TOP_UP_REASONS = [
  'Cash deposit',
  'Bank transfer',
  'Refund credit',
  'Promotional credit',
  'Loyalty reward',
  'Other',
]

const WalletModal: React.FC<WalletModalProps> = ({ customer, liveBalance, onClose, onTopUp }) => {
  const [tab, setTab] = useState<'topup' | 'history'>('topup')
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState(TOP_UP_REASONS[0])
  const [note, setNote] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [history, setHistory] = useState<WalletTx[]>([])
  const [isLoadingHistory, setIsLoadingHistory] = useState(false)
  const { showSuccess, showError } = useNotificationActions()

  const fetchHistory = useCallback(async () => {
    setIsLoadingHistory(true)
    try {
      const cid = customer.id || customer._id
      const res = await apiService.get<{ success: boolean; data: WalletTx[] }>(`/customers/${cid}/wallet/history`)
      const sorted = (res.data || []).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      setHistory(sorted)
    } catch {
      showError('History Load Failed', 'Could not load wallet transaction history. Try again later.')
      setHistory([])
    } finally {
      setIsLoadingHistory(false)
    }
  }, [customer])

  useEffect(() => {
    if (tab === 'history') fetchHistory()
  }, [tab, fetchHistory])

  const handleTopUp = async () => {
    const amt = parseFloat(amount)
    if (!amt || amt <= 0) return
    setIsSubmitting(true)
    try {
      const fullNote = note ? `${reason} — ${note}` : reason
      await onTopUp(amt, reason, fullNote)
      showSuccess('Wallet Topped Up', `Rs.${amt.toLocaleString()} added successfully.`)
      setAmount('')
      setNote('')
      // Refresh history if on history tab, or switch to show updated balance
      setTab('history')
      fetchHistory()
    } catch {
      showError('Top-up Failed', 'Could not update wallet balance.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const txTypeLabel = (type: string) => {
    if (type === 'topup') return { label: 'Top-Up', color: 'text-emerald-400', icon: <ArrowDownLeft className="w-3.5 h-3.5" /> }
    if (type === 'refund') return { label: 'Refund', color: 'text-blue-400', icon: <ArrowDownLeft className="w-3.5 h-3.5" /> }
    return { label: 'Deduction', color: 'text-red-400', icon: <ArrowUpRight className="w-3.5 h-3.5" /> }
  }

  const isDebit = (type: string) => type === 'deduct'
  const creditType = customer.customerType === 'special'
  const isOverdraft = liveBalance < 0

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
      <div className="glass-card w-full max-w-lg border-emerald-400/30 flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Wallet className="w-5 h-5 text-emerald-400" />
              <h2 className="text-xl font-bold text-emerald-400">E-Wallet</h2>
            </div>
            <p className="text-sm text-muted-foreground">{customer.name}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-muted-foreground uppercase tracking-widest mb-0.5">Current Balance</p>
            <p className={`text-2xl font-black font-mono ${isOverdraft ? 'text-red-400' : 'text-emerald-400'}`}>
              Rs.{liveBalance.toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
            {creditType && (
              <p className="text-[10px] text-purple-400 mt-0.5">Credit limit: Rs.{(customer.creditLimit || 0).toLocaleString()}</p>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border/50 mb-4">
          <button
            onClick={() => setTab('topup')}
            className={`px-4 py-2 text-sm font-bold transition-colors border-b-2 ${tab === 'topup' ? 'border-emerald-400 text-emerald-400' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          >
            Top-Up
          </button>
          <button
            onClick={() => setTab('history')}
            className={`px-4 py-2 text-sm font-bold transition-colors border-b-2 ${tab === 'history' ? 'border-emerald-400 text-emerald-400' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          >
            Transaction History
          </button>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {tab === 'topup' ? (
            <div className="space-y-4">
              {/* Quick amount buttons */}
              <div>
                <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest mb-2 block">Quick Amount</label>
                <div className="grid grid-cols-4 gap-2">
                  {[500, 1000, 2000, 5000].map(q => (
                    <button
                      key={q}
                      type="button"
                      onClick={() => setAmount(String(q))}
                      className={`py-2 text-sm font-bold rounded-lg border transition-colors ${amount === String(q) ? 'bg-emerald-400/20 border-emerald-400 text-emerald-400' : 'border-border text-muted-foreground hover:border-emerald-400/50 hover:text-emerald-400'}`}
                    >
                      Rs.{q.toLocaleString()}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest pl-1">Amount (Rs.)</label>
                <input
                  type="number"
                  min="1"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  className="w-full mt-1 px-4 py-3 bg-background/50 border border-border rounded-xl focus:ring-2 focus:ring-emerald-400 focus:outline-none font-mono text-lg"
                />
              </div>

              <div>
                <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest pl-1">Reason</label>
                <select
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="w-full mt-1 px-4 py-3 bg-background/50 border border-border rounded-xl focus:ring-2 focus:ring-emerald-400 focus:outline-none"
                >
                  {TOP_UP_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>

              <div>
                <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest pl-1">Additional Note (optional)</label>
                <input
                  type="text"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g. Receipt #1234"
                  className="w-full mt-1 px-4 py-3 bg-background/50 border border-border rounded-xl focus:ring-2 focus:ring-emerald-400 focus:outline-none"
                />
              </div>

              {/* Preview */}
              {amount && Number(amount) > 0 && (
                <div className="bg-emerald-400/5 border border-emerald-400/20 rounded-xl p-3 text-sm">
                  <div className="flex justify-between text-muted-foreground mb-1">
                    <span>Current balance</span>
                    <span className="font-mono">Rs.{liveBalance.toLocaleString('en-LK', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between text-emerald-400 mb-1">
                    <span>Top-up amount</span>
                    <span className="font-mono">+ Rs.{Number(amount).toLocaleString('en-LK', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between font-bold text-foreground border-t border-border/50 pt-1 mt-1">
                    <span>New balance</span>
                    <span className="font-mono text-emerald-400">Rs.{(liveBalance + Number(amount)).toLocaleString('en-LK', { minimumFractionDigits: 2 })}</span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs text-muted-foreground">{history.length} transactions</p>
                <button onClick={fetchHistory} className="text-xs text-primary flex items-center gap-1 hover:underline">
                  <RefreshCw className="w-3 h-3" /> Refresh
                </button>
              </div>
              {isLoadingHistory ? (
                <div className="py-8 text-center text-muted-foreground text-sm">Loading history...</div>
              ) : history.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground text-sm">No wallet transactions yet.</div>
              ) : (
                <div className="space-y-2">
                  {history.map((tx) => {
                    const { label, color, icon } = txTypeLabel(tx.type)
                    return (
                      <div key={tx.id} className="flex items-center justify-between p-3 bg-muted/10 rounded-lg border border-border/50">
                        <div className="flex items-center gap-2">
                          <div className={`${color} opacity-80`}>{icon}</div>
                          <div>
                            <p className={`text-xs font-bold ${color}`}>{label}</p>
                            <p className="text-[10px] text-muted-foreground">{tx.note || '—'}</p>
                            <p className="text-[10px] text-muted-foreground font-mono">{new Date(tx.createdAt).toLocaleString('en-LK')}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`font-bold font-mono text-sm ${isDebit(tx.type) ? 'text-red-400' : 'text-emerald-400'}`}>
                            {isDebit(tx.type) ? '−' : '+'}Rs.{tx.amount.toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                          </p>
                          <p className="text-[10px] text-muted-foreground font-mono">Bal: Rs.{tx.balance.toLocaleString('en-LK', { minimumFractionDigits: 2 })}</p>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer buttons */}
        <div className="flex gap-3 mt-4 pt-4 border-t border-border/50">
          <button onClick={onClose} className="flex-1 py-3 bg-muted hover:bg-muted/80 rounded-xl font-bold text-xs uppercase tracking-widest">
            Close
          </button>
          {tab === 'topup' && (
            <button
              disabled={isSubmitting || !amount || Number(amount) <= 0}
              onClick={handleTopUp}
              className="flex-1 py-3 bg-emerald-500 hover:bg-emerald-400 text-white rounded-xl font-bold text-xs uppercase tracking-widest disabled:opacity-50 transition-colors"
            >
              {isSubmitting ? 'Processing...' : `Top Up Rs.${Number(amount || 0).toLocaleString()}`}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Main CustomersPage ───────────────────────────────────────────────────────

export const CustomersPage: React.FC = () => {
  const {
    customers,
    isLoading,
    addCustomer,
    updateCustomer,
    deleteCustomer,
    topUpCustomerWallet,
  } = usePOSStore()

  const [walletCustomerId, setWalletCustomerId] = useState<string | null>(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingCustomer, setEditingCustomer] = useState<any | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState('All')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [addFormType, setAddFormType] = useState<string>('regular')
  const [editFormType, setEditFormType] = useState<string>('regular')

  const { showSuccess, showError } = useNotificationActions()

  // Always derive wallet customer from live store — never stale
  const walletCustomer = walletCustomerId
    ? customers.find(c => (c.id || c._id) === walletCustomerId) ?? null
    : null

  const filteredCustomers = customers.filter(customer => {
    const nameStr = customer.name || ''
    const emailStr = customer.email || ''
    const phoneStr = customer.phone || ''
    const term = searchTerm.trim().toLowerCase()
    const termDigits = term.replace(/\D/g, '')
    const matchesSearch = !term || nameStr.toLowerCase().includes(term) ||
      emailStr.toLowerCase().includes(term) ||
      (termDigits && phoneStr.replace(/\D/g, '').includes(termDigits))
    const status = customer.status || 'active'
    const matchesStatus = filterStatus === 'All' || status === filterStatus
    return matchesSearch && matchesStatus
  })

  const handleAddCustomer = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    const formData = new FormData(e.target as HTMLFormElement)
    const newCustomerType = (formData.get('customerType') as string) || 'regular'
    const customerData: any = {
      name: formData.get('name') as string,
      email: formData.get('email') as string,
      phone: formData.get('phone') as string,
      address: formData.get('address') as string,
      customerType: newCustomerType,
      status: 'active',
      totalSpent: 0,
      totalOrders: 0,
      walletBalance: 0,
      createdAt: new Date()
    }
    if (newCustomerType === 'special') {
      customerData.creditLimit = parseFloat(formData.get('creditLimit') as string) || 0
    }
    try {
      await addCustomer(customerData)
      showSuccess('Customer Added', `${customerData.name} has been registered successfully.`)
      setShowAddForm(false)
    } catch {
      showError('Registration Failed', 'Could not save customer information.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleEditCustomer = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingCustomer) return
    setIsSubmitting(true)
    const formData = new FormData(e.target as HTMLFormElement)
    const updatedType = (formData.get('customerType') as string) || 'regular'

    // Warn if changing from special to another type while customer has wallet debt
    if (editingCustomer.customerType === 'special' && updatedType !== 'special' && (editingCustomer.walletBalance || 0) < 0) {
      if (!confirm(`This customer owes Rs.${Math.abs(editingCustomer.walletBalance).toLocaleString()}. Converting to "${updatedType}" will prevent further credit purchases. Continue?`)) {
        setIsSubmitting(false)
        return
      }
    }

    const updates: any = {
      name: formData.get('name') as string,
      email: formData.get('email') as string,
      phone: formData.get('phone') as string,
      address: formData.get('address') as string,
      customerType: updatedType,
      creditLimit: updatedType === 'special' ? (parseFloat(formData.get('creditLimit') as string) || 0) : 0,
    }
    try {
      const customerId = editingCustomer.id || editingCustomer._id
      await updateCustomer(customerId, updates)
      showSuccess('Customer Updated', `${updates.name}'s information has been updated.`)
      setEditingCustomer(null)
    } catch {
      showError('Update Failed', 'Could not update customer information.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteCustomer = async (customer: any) => {
    if (!confirm(`Are you sure you want to delete ${customer.name}? This action cannot be undone.`)) return
    try {
      const customerId = customer.id || customer._id
      await deleteCustomer(customerId)
      showSuccess('Customer Deleted', `${customer.name} has been removed from the system.`)
    } catch {
      showError('Delete Failed', 'Could not delete customer.')
    }
  }

  const handleTopUp = async (amount: number, _reason: string, fullNote: string) => {
    if (!walletCustomerId) return
    await topUpCustomerWallet(walletCustomerId, amount, fullNote)
  }

  return (
    <div className="min-h-screen bg-gradient-space p-3 sm:p-4 lg:p-6">
      <div className="w-full">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6 sm:mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold esales-text font-display">Customer Relations</h1>
            <p className="text-muted-foreground mt-2">Manage your bookstore's loyal readers and VIPs.</p>
          </div>
          <button onClick={() => setShowAddForm(true)} className="glow-button">
            <span className="mr-2">👤</span> Add Customer
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
          {[
            { label: 'Total Members', value: customers.length, icon: '👥', color: 'text-primary' },
            { label: 'VIP Readers', value: customers.filter(c => c.status === 'vip').length, icon: '⭐', color: 'text-yellow-400' },
            { label: 'Active This Month', value: customers.filter(c => (c.status || 'active') === 'active').length, icon: '🔥', color: 'text-green-400' },
            { label: 'Total Wallet Balance', value: `Rs.${customers.reduce((sum, c) => sum + (c.walletBalance || 0), 0).toLocaleString()}`, icon: '💳', color: 'text-emerald-400' }
          ].map((stat, i) => (
            <div key={i} className="glass-card">
              <div className="flex justify-between items-start mb-2">
                <span className="text-2xl">{stat.icon}</span>
                <span className={`text-2xl font-bold ${stat.color}`}>{stat.value}</span>
              </div>
              <p className="text-xs text-muted-foreground uppercase tracking-widest font-bold font-sans">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="glass-card mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <input
                type="text"
                placeholder="Search customers by name, email or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-3 bg-background/50 border border-border rounded-lg placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:outline-none"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 bg-background/50 border border-border rounded-lg"
            >
              <option value="All">All Statuses</option>
              <option value="active">Active Members</option>
              <option value="inactive">Inactive</option>
              <option value="vip">VIP Readers</option>
            </select>
          </div>
        </div>

        {/* Customers Table / Cards */}
        <div className="glass-card overflow-hidden">
          {isLoading && customers.length === 0 ? (
            <LoadingSpinner size="md" message="Loading Customers" submessage="Syncing member database..." />
          ) : (
            <>
            {/* Mobile card view */}
            <div className="md:hidden space-y-3">
              {filteredCustomers.length === 0 ? (
                <div className="py-12 text-center text-muted-foreground italic">No members match your current filters.</div>
              ) : (
                filteredCustomers.map(customer => (
                  <div key={customer.id || customer._id} className="p-4 rounded-xl border border-border bg-background/50">
                    <div className="flex justify-between items-start gap-2 mb-2">
                      <div className="min-w-0">
                        <p className="font-bold text-foreground truncate">{customer.name}</p>
                        <p className="text-xs text-muted-foreground">{customer.email || customer.phone || 'No contact'}</p>
                      </div>
                      <span className={`px-2 py-0.5 text-[10px] font-black uppercase rounded-full border flex-shrink-0 ${customer.customerType === 'wholesale' ? 'text-blue-400 bg-blue-400/10 border-blue-400/30' : customer.customerType === 'special' ? 'text-purple-400 bg-purple-400/10 border-purple-400/30' : 'text-green-400 bg-green-400/10 border-green-400/30'}`}>
                        {customer.customerType || 'regular'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-2 text-xs">
                      <div className="flex items-center gap-3">
                        <span className="text-muted-foreground">Orders: <span className="font-mono font-bold text-foreground">{customer.totalOrders || 0}</span></span>
                        <span className="text-muted-foreground">Spent: <span className="font-bold text-primary">Rs.{(customer.totalSpent || 0).toLocaleString()}</span></span>
                      </div>
                      <span className={`font-bold font-mono ${(customer.walletBalance || 0) < 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                        Rs.{(customer.walletBalance || 0).toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-3 pt-2 border-t border-border/30">
                      <button onClick={() => setWalletCustomerId(customer.id || customer._id)} className="flex-1 flex items-center justify-center gap-1 text-[10px] font-bold text-emerald-400 border border-emerald-400/30 rounded-lg px-2 py-1.5 hover:bg-emerald-400/10 transition-colors">
                        <Wallet className="w-3 h-3" /> WALLET
                      </button>
                      <button onClick={() => { setEditingCustomer(customer); setEditFormType(customer.customerType || 'regular') }} className="flex-1 text-[10px] font-bold text-primary border border-primary/30 rounded-lg px-2 py-1.5 hover:bg-primary/10 transition-colors">
                        EDIT
                      </button>
                      <button onClick={() => handleDeleteCustomer(customer)} className="flex-1 text-[10px] font-bold text-red-400 border border-red-400/30 rounded-lg px-2 py-1.5 hover:bg-red-400/10 transition-colors">
                        DELETE
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Desktop table view */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-border text-muted-foreground text-xs uppercase tracking-widest">
                    <th className="px-6 py-4 font-bold">Member Information</th>
                    <th className="px-6 py-4 font-bold">Contact Details</th>
                    <th className="px-6 py-4 font-bold text-center">Type</th>
                    <th className="px-6 py-4 font-bold text-center">Orders</th>
                    <th className="px-6 py-4 font-bold">Total Spent</th>
                    <th className="px-6 py-4 font-bold">Wallet</th>
                    <th className="px-6 py-4 font-bold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/50">
                  {filteredCustomers.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-12 text-center text-muted-foreground italic">No members match your current filters.</td>
                    </tr>
                  ) : (
                    filteredCustomers.map(customer => (
                      <tr key={customer.id || customer._id} className="hover:bg-muted/10 transition-colors">
                        <td className="px-6 py-4">
                          <div>
                            <div className="font-bold text-foreground">{customer.name}</div>
                            <div className="text-xs text-muted-foreground truncate max-w-[200px]">{customer.address || 'Address not listed'}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm">{customer.email}</div>
                          <div className="text-xs text-muted-foreground">{customer.phone}</div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className={`px-2 py-0.5 text-[10px] font-black uppercase rounded-full border ${customer.customerType === 'wholesale' ? 'text-blue-400 bg-blue-400/10 border-blue-400/30' : customer.customerType === 'special' ? 'text-purple-400 bg-purple-400/10 border-purple-400/30' : 'text-green-400 bg-green-400/10 border-green-400/30'}`}>
                            {customer.customerType || 'regular'}{customer.customerType === 'special' && customer.creditLimit ? ` · Rs.${Number(customer.creditLimit).toLocaleString()} limit` : ''}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="bg-muted px-2 py-1 rounded text-xs font-mono">{customer.totalOrders || 0}</span>
                        </td>
                        <td className="px-6 py-4 font-bold text-primary">
                          Rs.{(customer.totalSpent || 0).toLocaleString()}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <span className={`font-bold text-sm font-mono ${(customer.walletBalance || 0) < 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                              Rs.{(customer.walletBalance || 0).toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                            </span>
                            <button
                              onClick={() => setWalletCustomerId(customer.id || customer._id)}
                              className="flex items-center gap-1 text-[10px] font-bold text-emerald-400 border border-emerald-400/30 rounded px-1.5 py-0.5 hover:bg-emerald-400/10 transition-colors"
                              title="Manage wallet"
                            >
                              <Wallet className="w-3 h-3" /> MANAGE
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => { setEditingCustomer(customer); setEditFormType(customer.customerType || 'regular') }}
                              className="text-xs font-bold text-primary hover:underline"
                            >
                              EDIT
                            </button>
                            <button
                              onClick={() => handleDeleteCustomer(customer)}
                              className="text-xs font-bold text-red-400 hover:underline"
                            >
                              DELETE
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>

        {/* Add Modal */}
        {showAddForm && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
            <div className="glass-card w-full max-w-md border-primary/30">
              <h2 className="text-2xl font-bold esales-text mb-6">Register New Member</h2>
              <form onSubmit={handleAddCustomer} className="space-y-4">
                <div>
                  <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest pl-1">Full Name</label>
                  <input name="name" required className="w-full mt-1 px-4 py-3 bg-background/50 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:outline-none" />
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest pl-1">Email Address</label>
                  <input name="email" type="email" required className="w-full mt-1 px-4 py-3 bg-background/50 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:outline-none" />
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest pl-1">Phone Number</label>
                  <input name="phone" required className="w-full mt-1 px-4 py-3 bg-background/50 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:outline-none" />
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest pl-1">Mailing Address</label>
                  <input name="address" required className="w-full mt-1 px-4 py-3 bg-background/50 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:outline-none" />
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest pl-1">Customer Type</label>
                  <select name="customerType" value={addFormType} onChange={e => setAddFormType(e.target.value)} className="w-full mt-1 px-4 py-3 bg-background/50 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:outline-none">
                    <option value="regular">Regular Customer</option>
                    <option value="wholesale">Wholesale Customer</option>
                    <option value="special">Special Customer</option>
                  </select>
                </div>
                {addFormType === 'special' && (
                  <div>
                    <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest pl-1">Credit Limit (Rs.)</label>
                    <input name="creditLimit" type="number" min="0" step="100" defaultValue="0" className="w-full mt-1 px-4 py-3 bg-background/50 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:outline-none" placeholder="Max debt allowed e.g. 5000" />
                    <p className="text-[10px] text-muted-foreground pl-1 mt-1">Wallet can go negative up to this limit.</p>
                  </div>
                )}
                <div className="flex gap-4 mt-8">
                  <button type="button" onClick={() => { setShowAddForm(false); setAddFormType('regular') }} className="flex-1 py-4 bg-muted hover:bg-muted/80 rounded-xl font-bold text-xs uppercase tracking-widest">Discard</button>
                  <button type="submit" disabled={isSubmitting} className="flex-1 py-4 bg-primary text-white rounded-xl font-bold text-xs uppercase tracking-widest glow-effect">
                    {isSubmitting ? 'WORKING...' : 'REGISTER'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {editingCustomer && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
            <div className="glass-card w-full max-w-md border-primary/30">
              <h2 className="text-2xl font-bold esales-text mb-6">Edit Customer</h2>
              <form onSubmit={handleEditCustomer} className="space-y-4">
                <div>
                  <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest pl-1">Full Name</label>
                  <input name="name" defaultValue={editingCustomer.name} required className="w-full mt-1 px-4 py-3 bg-background/50 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:outline-none" />
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest pl-1">Email Address</label>
                  <input name="email" type="email" defaultValue={editingCustomer.email} required className="w-full mt-1 px-4 py-3 bg-background/50 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:outline-none" />
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest pl-1">Phone Number</label>
                  <input name="phone" defaultValue={editingCustomer.phone} required className="w-full mt-1 px-4 py-3 bg-background/50 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:outline-none" />
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest pl-1">Mailing Address</label>
                  <input name="address" defaultValue={editingCustomer.address} required className="w-full mt-1 px-4 py-3 bg-background/50 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:outline-none" />
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest pl-1">Customer Type</label>
                  <select name="customerType" value={editFormType} onChange={e => setEditFormType(e.target.value)} className="w-full mt-1 px-4 py-3 bg-background/50 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:outline-none">
                    <option value="regular">Regular Customer</option>
                    <option value="wholesale">Wholesale Customer</option>
                    <option value="special">Special Customer</option>
                  </select>
                </div>
                {editFormType === 'special' && (
                  <div>
                    <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest pl-1">Credit Limit (Rs.)</label>
                    <input name="creditLimit" type="number" min="0" step="100" defaultValue={editingCustomer.creditLimit || 0} className="w-full mt-1 px-4 py-3 bg-background/50 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:outline-none" placeholder="Max debt allowed e.g. 5000" />
                    <p className="text-[10px] text-muted-foreground pl-1 mt-1">Wallet can go negative up to this limit.</p>
                  </div>
                )}
                <div className="flex gap-4 mt-8">
                  <button type="button" onClick={() => setEditingCustomer(null)} className="flex-1 py-4 bg-muted hover:bg-muted/80 rounded-xl font-bold text-xs uppercase tracking-widest">Cancel</button>
                  <button type="submit" disabled={isSubmitting} className="flex-1 py-4 bg-primary text-white rounded-xl font-bold text-xs uppercase tracking-widest glow-effect">
                    {isSubmitting ? 'SAVING...' : 'SAVE CHANGES'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Wallet Management Modal */}
        {walletCustomer && (
          <WalletModal
            customer={walletCustomer}
            liveBalance={walletCustomer.walletBalance || 0}
            onClose={() => setWalletCustomerId(null)}
            onTopUp={handleTopUp}
          />
        )}
      </div>
    </div>
  )
}
